"use client";

import * as React from "react";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight } from "lucide-react";
import { BorderBeam } from "border-beam";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import castingLoopMp4 from "@/assets/casting-hero-loop.mp4.asset.json";
import castingLoopWebm from "@/assets/casting-hero-loop.webm.asset.json";

type ImageStreamHeroProps = React.ComponentProps<"section"> & {
  socialProof?: React.ReactNode;
};

/**
 * Hero minimalista: título, descrição, CTA e prova social sobre o fundo
 * próprio da abertura (--landing-hero), mais fechado que as demais dobras.
 */
export function ImageStreamHero({
  socialProof,
  className,
  ...props
}: ImageStreamHeroProps) {
  return (
    <section
      aria-label="O ritual do vinil agora coletivo"
      className={cn(
        "relative flex min-h-svh w-full flex-col items-center justify-center gap-6 bg-landing-hero px-5 pb-14 pt-[15svh] text-center text-foreground sm:pt-[13svh]",
        className,
      )}
      {...props}
    >
      <div className="max-w-2xl">
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          disablePictureInPicture
          controlsList="nodownload"
          tabIndex={-1}
          aria-hidden="true"
          className="pointer-events-none mx-auto mb-6 aspect-square w-[clamp(140px,21vw,200px)] select-none object-cover [mask-image:radial-gradient(ellipse_at_center,#000_48%,transparent_88%)]"
        >
          <source src={castingLoopWebm.url} type="video/webm" />
          <source src={castingLoopMp4.url} type="video/mp4" />
        </video>
        <h1 className="text-balance font-sans text-4xl font-medium leading-[1.02] sm:text-5xl lg:text-6xl">
          O ritual do vinil,
          <br />
          agora coletivo.
        </h1>

        <p className="mx-auto mt-5 max-w-md text-pretty text-sm leading-relaxed text-foreground/70 sm:text-base">
          Organize seus discos, compartilhe sua coleção e descubra o que outros
          colecionadores estão ouvindo.
        </p>

        <div className="md:hidden">
          <Button
            asChild
            size="lg"
            className="rounded-full bg-action px-7 text-action-foreground hover:bg-action/90"
          >
            <Link to="/explore">
              Comece grátis
              <ArrowUpRight />
            </Link>
          </Button>
        </div>
        <BorderBeam
          size="pulse-outside"
          colorVariant="colorful"
          strength={0.9}
          theme="light"
          borderRadius={22}
          className="mt-7 hidden md:inline-block"
        >
          <Button
            asChild
            size="lg"
            className="rounded-full bg-action px-7 text-action-foreground hover:bg-action/90"
          >
            <Link to="/explore">
              Comece grátis
              <ArrowUpRight />
            </Link>
          </Button>
        </BorderBeam>

        {socialProof ? (
          <div className="mt-5 flex justify-center">{socialProof}</div>
        ) : null}
      </div>
    </section>
  );
}
